import {Inject, Injectable, Injector, PLATFORM_ID} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BaseSiteService, EventService} from '@spartacus/core';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {catchError, first} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DcpErrorHandlingUtil} from '../utils';
import {DCPErrorEvent, FeatureToggleConfig} from '../models';
import {FeatureTogglesService} from './feature-toggles.service';
import token from '../token/tokens';
import {ConfigService} from './config.service';
import {KymaService} from './kyma.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpLoggingService {
  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    @Inject(token.values.FEATURE_TOGGLES)
    private featureToggles: FeatureToggleConfig,
    private http: HttpClient,
    private baseSiteService: BaseSiteService,
    private injector: Injector,
    private eventService: EventService,
  ) {}

  /**
   * Log info message in dcp-lambda-log-commerce-ui
   *
   * @param msg - json object with message:string and body:json fields
   * @param source
   */
  public info(msg: any, source: any): Observable<boolean> {
    const subject: Subject<boolean> = new Subject();

    combineLatest([this.baseSiteService.getActive(), this.injector.get(KymaService).prepareAuthConfig()])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([baseSite, options]) => {
        this.injector
          .get(FeatureTogglesService)
          .isEnabled(this.featureToggles.globalInfoHandler)
          .pipe(untilDestroyed(this))
          .subscribe((isEnabled) => {
            if (isEnabled) {
              const internalInfoMessage = DcpErrorHandlingUtil.mapToInternalInfo(msg);
              const requestData = {
                source,
                platformId: this.platformId,
                baseSite,
                info: internalInfoMessage,
              };

              this.http
                .post(this.injector.get(KymaService).getUrl('log'), requestData, options)
                .pipe(
                  first(),
                  catchError((e) => {
                    subject.next(true);
                    return of(true);
                  }),
                  untilDestroyed(this),
                )
                .subscribe(() => subject.next(true));
            }
          });
      });

    return subject.asObservable();
  }

  public error(errorData: any, source: any): Observable<boolean> {
    const subject: Subject<boolean> = new Subject();

    combineLatest([this.baseSiteService.getActive(), this.injector.get(KymaService).prepareAuthConfig()])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([baseSite, options]) => {
        const internalError = DcpErrorHandlingUtil.mapToInternalError(errorData);
        this.eventService.dispatch(new DCPErrorEvent(internalError));

        this.injector
          .get(FeatureTogglesService)
          .isEnabled(this.featureToggles.globalErrorHandler)
          .pipe(untilDestroyed(this))
          .subscribe((isEnabled) => {
            if (
              isEnabled &&
              !errorData?.url?.includes(this.injector.get(ConfigService).getString('kyma.namespace.log'))
            ) {
              this.http
                .post(
                  this.injector.get(KymaService).getUrl('log'),
                  {
                    source,
                    platformId: this.platformId,
                    baseSite,
                    error: internalError,
                  },
                  options,
                )
                .pipe(
                  first(),
                  catchError((e) => {
                    console.error(e); //NOSONAR
                    subject.next(true);
                    return of(true);
                  }),
                  untilDestroyed(this),
                )
                .subscribe(() => subject.next(true));
            }
          });
      });
    return subject.asObservable();
  }
}
